import { z } from 'zod';

// ---------------------------------------------------------------------------
// Update User
// ---------------------------------------------------------------------------

export const updateUserBody = z.object({
  user_language: z.string().optional(),
  ui_dark_mode: z.boolean().optional(),
  profile_name: z.string().optional(),
  link_email_to_identities: z.boolean().optional(),
}).refine(
  (data) => Object.values(data).some((v) => v !== undefined),
  { message: 'At least one field must be provided' },
);

// ---------------------------------------------------------------------------
// Update Profile
// ---------------------------------------------------------------------------

export const updateProfileBody = z.object({
  display_name: z.string().optional(),
  avatar_url: z.string().optional(),
  preferences: z.record(z.unknown()).optional(),
}).refine(
  (data) => Object.values(data).some((v) => v !== undefined),
  { message: 'At least one field must be provided' },
);

// ---------------------------------------------------------------------------
// Update Preferences (PATCH /users/preferences)
// ---------------------------------------------------------------------------

// Pathnames only. Leading `/` required; `(?!\/)` rejects `//evil.com` scheme-relative URLs.
const pathnameRegex = /^\/(?!\/)[A-Za-z0-9\-._~!$&'()*+,;=:@/?%]*$/;

export const updatePreferencesBody = z.object({
  last_viewed_nav: z.string().regex(pathnameRegex, 'Must be a URL pathname').max(2048).optional(),
  dismissed_agentic_shortcut: z.boolean().optional(),
}).passthrough();

export type UpdatePreferencesBody = z.infer<typeof updatePreferencesBody>;

// ---------------------------------------------------------------------------
// Push tokens (POST/DELETE /users/me/push-token) — mobile push registration
// ---------------------------------------------------------------------------

// Expo push token shape: `ExponentPushToken[…]` or `ExpoPushToken[…]`. We accept the
// raw string (validation is a soft prefix check) so a future bare FCM/APNs token
// isn't rejected; `platform` records the origin.
export const registerPushTokenBody = z.object({
  token: z.string().min(1).max(512),
  platform: z.enum(['ios', 'android']),
  /** Stable per-install id so re-registration replaces the prior token for the device. */
  device_id: z.string().min(1).max(128).optional(),
});

// DELETE carries the token as a query param (the Transport `delete` has no body).
export const deletePushTokenQuery = z.object({
  token: z.string().min(1).max(512),
});

export type RegisterPushTokenBody = z.infer<typeof registerPushTokenBody>;
export type DeletePushTokenQuery = z.infer<typeof deletePushTokenQuery>;

/** POST /users/me/push-token */
export interface RegisterPushTokenResponse {
  success: true;
  data: { registered: true };
}

/** DELETE /users/me/push-token */
export interface DeletePushTokenResponse {
  success: true;
  data: { deleted: true };
}

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

export const activityQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

// ---------------------------------------------------------------------------
// Account Deletion
// ---------------------------------------------------------------------------

/** Two modes — Scheduled (30-day grace, undoable) or Immediate (no undo). */
export const deletionModeSchema = z.enum(['scheduled', 'immediate']);

export const deletionRequestBody = z.object({
  mode: deletionModeSchema,
  /** User-supplied reason captured in the audit log; bounded to bound logged PII. */
  reason: z.string().max(500).optional(),
  /** Type-to-confirm value; must equal the user's email (constant-time compared). */
  email_confirmation: z.string().min(1).max(320),
});

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type UpdateUserBody = z.infer<typeof updateUserBody>;
export type UpdateProfileBody = z.infer<typeof updateProfileBody>;
export type ActivityQuery = z.infer<typeof activityQuery>;
export type DeletionMode = z.infer<typeof deletionModeSchema>;
export type DeletionRequestBody = z.infer<typeof deletionRequestBody>;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

/** Navigation hints — which dashboard sections the user has hub access for */
export interface NavHints {
  has_chat: boolean;
  has_task: boolean;
  has_support: boolean;
}

/** Account deletion lifecycle as seen by the user-facing API. */
export type DeletionStatus = 'none' | 'grace' | 'purging' | 'purged';

/** User data returned by GET/PATCH /users/me */
export interface UserDataResponse {
  user_id: string;
  user_language: string | null;
  ui_dark_mode: boolean | null;
  profile_name: string | null;
  link_email_to_identities: boolean | null;
  is_platform_admin: boolean;
  created_at: string;
  user_name: string | null;
  account_age_hours: number;
  team_ids: string[];
  nav_hints: NavHints;
  dismissed_agentic_shortcut: boolean;
  /** Active deletion lifecycle; `'none'` for the common case. */
  deletion_status: DeletionStatus;
  /** ISO-8601 timestamp when the cron will purge; null unless status='grace'. */
  deletion_scheduled_purge_at: string | null;
}

/** GET /users/me or PATCH /users/me */
export interface GetUserResponse {
  success: true;
  data: UserDataResponse;
}

/** Profile data returned by GET/PATCH /users/profile */
export interface ProfileDataResponse {
  user_id: string;
  display_name: string | null;
  email: string | null;
  avatar_url: string | null;
  preferences: Record<string, unknown> | null;
  created_at: string;
  updated_at: string | null;
}

/** GET /users/profile or PATCH /users/profile */
export interface GetProfileResponse {
  data: ProfileDataResponse;
}

export interface UserPreferences {
  last_viewed_nav?: string;
  dismissed_agentic_shortcut?: boolean;
  [key: string]: unknown;
}

/** GET /users/preferences */
export interface GetPreferencesResponse {
  data: UserPreferences;
}

/** PATCH /users/preferences */
export interface UpdatePreferencesResponse {
  data: UserPreferences;
}

/** Activity log item */
export interface ActivityLogItem {
  activity_id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/** GET /users/activity */
export interface GetActivityResponse {
  data: ActivityLogItem[];
}

// ---------------------------------------------------------------------------
// Account Deletion — responses
// ---------------------------------------------------------------------------

/** Why the deletion preflight blocks the user from proceeding. */
export type DeletionBlocker =
  | {
      type: 'org_owner';
      org_id: string;
      org_name: string;
      has_active_subscription: boolean;
    }
  | {
      type: 'sole_admin';
      org_id: string;
      org_name: string;
      has_active_subscription: boolean;
    };

export interface DeletionSimpleRemoval {
  org_id: string;
  org_name: string;
}

export interface DeletionHubGrant {
  hub_id: string;
  hub_name: string | null;
  role: 'admin' | 'user' | 'team_user';
}

/** GET /api/users/me/deletion/preflight */
export interface DeletionPreflightResponse {
  blocking: DeletionBlocker[];
  /** Orgs where the user is one admin among several — losing the user is a simple grant removal. */
  simple_removals: DeletionSimpleRemoval[];
  /** Hub-level grants without an associated admin role on the parent org. */
  hub_only_grants: DeletionHubGrant[];
  identity_count: number;
  conversation_count_estimate: number;
}

/** POST /api/users/me/deletion — accepted; describes the resulting state. */
export interface DeletionRequestResponse {
  status: Extract<DeletionStatus, 'grace' | 'purging'>;
  scheduled_purge_at: string | null;
}

/** DELETE /api/users/me/deletion — undo response. */
export interface DeletionAbortResponse {
  status: 'none';
}
